import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COUNTRIES = [
  'United States', 'United Kingdom', 'Canada', 'Australia', 'India', 
  'Germany', 'France', 'Japan', 'China', 'Brazil', 'Mexico', 'Spain',
  'Italy', 'South Africa', 'Singapore', 'New Zealand', 'Other'
];

const InstitutionRegistration = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [institutionKey, setInstitutionKey] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    street_address: '',
    city: '',
    country: 'United States'
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/institution/register`, formData);
      setInstitutionKey(response.data.institution_key);
      toast.success('Institution registered successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  if (institutionKey) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-2xl p-8 backdrop-blur-sm bg-white/90 border-0 shadow-2xl">
          <div className="text-center">
            <div className="w-20 h-20 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full mx-auto mb-6 flex items-center justify-center">
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Registration Successful!</h2>
            <p className="text-gray-600 mb-6">Your institution has been registered successfully.</p>
            
            <div className="bg-emerald-50 border-2 border-emerald-200 rounded-lg p-6 mb-6">
              <p className="text-sm text-gray-600 mb-2">Your Institution Key:</p>
              <div className="flex items-center justify-center gap-3 mb-4">
                <code className="text-3xl font-bold text-emerald-700" data-testid="institution-key-display">
                  {institutionKey}
                </code>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(institutionKey);
                    toast.success('Key copied to clipboard!');
                  }}
                  className="p-2 hover:bg-emerald-100 rounded-lg transition-colors"
                  data-testid="copy-key-button"
                >
                  <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </button>
              </div>
              <p className="text-sm text-gray-700">
                📧 This key has been sent to <strong>{formData.email}</strong>
              </p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-left">
              <h3 className="font-semibold text-blue-900 mb-2">Important Instructions:</h3>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Share this key with all supervisors who need to register</li>
                <li>• Supervisors must enter this key during their sign-up process</li>
                <li>• Keep this key secure - do not share publicly</li>
                <li>• You can view this key anytime in your institution dashboard</li>
              </ul>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={() => navigate('/institution-login')}
                className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white"
                data-testid="go-to-login-button"
              >
                Go to Login
              </Button>
              <Button
                onClick={() => {
                  setInstitutionKey('');
                  setFormData({
                    name: '',
                    phone: '',
                    email: '',
                    street_address: '',
                    city: '',
                    country: 'United States'
                  });
                }}
                variant="outline"
                data-testid="register-another-button"
              >
                Register Another Institution
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-100 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl mb-4 shadow-lg">
            <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Institution Registration
          </h1>
          <p className="text-gray-600">Register your educational institution to get started</p>
        </div>

        <Card className="backdrop-blur-sm bg-white/90 border-0 shadow-2xl p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="inst-name">Institution Name *</Label>
              <Input
                id="inst-name"
                type="text"
                placeholder="Springfield High School"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
                data-testid="institution-name-input"
                className="mt-1"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="inst-phone">Contact Phone Number *</Label>
                <Input
                  id="inst-phone"
                  type="tel"
                  placeholder="+1 234 567 8900"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  required
                  data-testid="institution-phone-input"
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="inst-email">Official Email Address *</Label>
                <Input
                  id="inst-email"
                  type="email"
                  placeholder="admin@school.edu"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                  data-testid="institution-email-input"
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="inst-address">Street Address *</Label>
              <Input
                id="inst-address"
                type="text"
                placeholder="123 Main Street"
                value={formData.street_address}
                onChange={(e) => setFormData({...formData, street_address: e.target.value})}
                required
                data-testid="institution-address-input"
                className="mt-1"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="inst-city">City/Suburb *</Label>
                <Input
                  id="inst-city"
                  type="text"
                  placeholder="Springfield"
                  value={formData.city}
                  onChange={(e) => setFormData({...formData, city: e.target.value})}
                  required
                  data-testid="institution-city-input"
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="inst-country">Country *</Label>
                <select
                  id="inst-country"
                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  value={formData.country}
                  onChange={(e) => setFormData({...formData, country: e.target.value})}
                  required
                  data-testid="institution-country-select"
                >
                  {COUNTRIES.map((country) => (
                    <option key={country} value={country}>{country}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm text-amber-800">
                <strong>Note:</strong> After registration, you will receive a unique Institution Key via email. 
                This key must be shared with all supervisors who need to register from your institution.
              </p>
            </div>

            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white"
              disabled={loading}
              data-testid="submit-registration-button"
            >
              {loading ? 'Registering...' : 'Register Institution'}
            </Button>

            <div className="text-center">
              <button
                type="button"
                onClick={() => navigate('/')}
                className="text-sm text-gray-600 hover:text-gray-900"
                data-testid="back-to-home-button"
              >
                ← Back to Home
              </button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default InstitutionRegistration;
